import React from 'react';
import { View, Text, StyleSheet, Image, TouchableOpacity, StyleProp, ViewStyle } from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import { REDESIGN_TYPOGRAPHY, REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { GlassCard } from '../GlassCard';

const CheckIcon = RESIDENTS_SVG_COMPONENTS.CHECK;

interface AutopayCardProps {
  displayName: string;
  initials: string;
  photoUrl?: string;
  propertyName: string;
  autopayEnabled: boolean;
  amount?: string;
  paymentMethodLabel?: string;
  startDate?: string;
  endDate?: string;
  processedDay?: string;
  onSetup?: () => void;
  onEdit?: () => void;
  onRemove?: () => void;
  isSetupDisabled?: boolean;
  style?: StyleProp<ViewStyle>;
}

interface InfoRowProps {
  label: string;
  value: string;
  dotColor?: string;
  labelColor: string;
  valueColor: string;
}

const InfoRow: React.FC<InfoRowProps> = React.memo(({
  label,
  value,
  dotColor,
  labelColor,
  valueColor,
}) => (
  <View style={styles.infoRow}>
    <View style={styles.infoRowLeft}>
      <View style={[styles.infoDot, { backgroundColor: dotColor }]} />
      <Text style={[styles.infoLabel, { color: labelColor }]}>{label}</Text>
    </View>
    <Text style={[styles.infoValue, { color: valueColor }]} numberOfLines={1}>{value}</Text>
  </View>
));

InfoRow.displayName = 'InfoRow';

export const AutopayCard: React.FC<AutopayCardProps> = React.memo(({
  displayName,
  initials,
  photoUrl,
  propertyName,
  autopayEnabled,
  amount,
  paymentMethodLabel,
  startDate,
  endDate,
  processedDay = '1st of each month',
  onSetup,
  onEdit,
  onRemove,
  isSetupDisabled = false,
  style,
}) => {
  const { colors, semantic } = useThemeColors();

  const dotBlue = semantic.interactive.primary;
  const dotGreen = colors.accent.green;
  const dotNeutral = colors.text.dimmer;

  return (
    <GlassCard style={style} borderRadius={16} padding={20}>
      <View style={styles.header}>
        <View style={styles.avatarOuter}>
          {photoUrl ? (
            <Image source={{ uri: photoUrl }} style={styles.avatarImage} />
          ) : (
            <LinearGradient
              colors={['rgba(71,115,254,0.25)', 'rgba(190,94,200,0.25)']}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={styles.avatarGradient}
            >
              <Text style={[styles.avatarInitials, { color: colors.text.tertiary }]}>{initials}</Text>
            </LinearGradient>
          )}
        </View>
        <View style={styles.headerText}>
          <Text style={[styles.userName, { color: colors.text.primary }]} numberOfLines={1}>{displayName}</Text>
          <Text style={[styles.propertyName, { color: colors.text.dim }]} numberOfLines={1}>{propertyName}</Text>
        </View>
      </View>

      {autopayEnabled ? (
        <>
          <View style={styles.statusRow}>
            <View style={[styles.statusCheck, { backgroundColor: colors.accent.greenBg }]}>
              {CheckIcon && <CheckIcon width={10} height={10} color={colors.accent.green} />}
            </View>
            <Text style={[styles.statusText, { color: colors.accent.green }]}>Autopay active</Text>
          </View>

          <View style={[styles.divider, { backgroundColor: colors.separator }]} />

          <View style={styles.detailsContainer}>
            {amount != null && (
              <InfoRow label="Amount" value={amount} dotColor={dotBlue} labelColor={colors.text.muted} valueColor={colors.text.tertiary} />
            )}
            {paymentMethodLabel && (
              <InfoRow label="Payment method" value={paymentMethodLabel} dotColor={dotGreen} labelColor={colors.text.muted} valueColor={colors.text.tertiary} />
            )}
            {startDate && (
              <InfoRow label="Start date" value={startDate} dotColor={dotNeutral} labelColor={colors.text.muted} valueColor={colors.text.tertiary} />
            )}
            {endDate && (
              <InfoRow label="End date" value={endDate} dotColor={dotNeutral} labelColor={colors.text.muted} valueColor={colors.text.tertiary} />
            )}
            <InfoRow label="Processed" value={processedDay} dotColor={dotNeutral} labelColor={colors.text.muted} valueColor={colors.text.tertiary} />
          </View>

          <View style={styles.actionsRow}>
            {onEdit && (
              <TouchableOpacity style={[styles.actionButton, { backgroundColor: colors.glass.buttonBg, borderColor: colors.glass.buttonBorder }]} onPress={onEdit} activeOpacity={0.7}>
                <Text style={[styles.actionButtonText, { color: semantic.interactive.primary }]}>Edit settings</Text>
              </TouchableOpacity>
            )}
            {onRemove && (
              <TouchableOpacity style={styles.removeButton} onPress={onRemove} activeOpacity={0.7}>
                <Text style={[styles.removeButtonText, { color: colors.accent.red }]}>Remove</Text>
              </TouchableOpacity>
            )}
          </View>
        </>
      ) : (
        <>
          <View style={[styles.divider, { backgroundColor: colors.separator }]} />
          <View style={styles.setupContainer}>
            <Text style={[styles.setupDescription, { color: colors.text.dim }]}>
              Automate your rent payments
            </Text>
            <TouchableOpacity
              style={[styles.setupButton, { backgroundColor: semantic.interactive.primary }, isSetupDisabled && styles.setupButtonDisabled]}
              onPress={onSetup}
              disabled={isSetupDisabled}
              activeOpacity={0.8}
            >
              <Text style={styles.setupButtonText}>Setup Autopay</Text>
            </TouchableOpacity>
          </View>
        </>
      )}
    </GlassCard>
  );
});

AutopayCard.displayName = 'AutopayCard';

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
  },
  avatarOuter: {
    width: 48,
    height: 48,
    borderRadius: 24,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    overflow: 'hidden',
  },
  avatarImage: {
    width: 48,
    height: 48,
  },
  avatarGradient: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarInitials: {
    fontSize: 18,
    fontWeight: '700',
    letterSpacing: -0.3,
  },
  headerText: {
    flex: 1,
    gap: 2,
  },
  userName: {
    ...REDESIGN_TYPOGRAPHY.cardTitleLarge,
  },
  propertyName: {
    ...REDESIGN_TYPOGRAPHY.caption,
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 14,
  },
  statusCheck: {
    width: 16,
    height: 16,
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
  },
  statusText: {
    ...REDESIGN_TYPOGRAPHY.captionMedium,
  },
  divider: {
    height: 1,
    marginVertical: 16,
  },
  detailsContainer: {
    backgroundColor: 'rgba(255,255,255,0.02)',
    borderRadius: 12,
    padding: 14,
    gap: 10,
    marginBottom: 16,
  },
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  infoRowLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  infoDot: {
    width: 5,
    height: 5,
    borderRadius: 2.5,
  },
  infoLabel: {
    ...REDESIGN_TYPOGRAPHY.caption,
  },
  infoValue: {
    ...REDESIGN_TYPOGRAPHY.caption,
    fontWeight: '500',
    flexShrink: 1,
    textAlign: 'right',
  },
  actionsRow: {
    flexDirection: 'row',
    gap: 10,
  },
  actionButton: {
    flex: 1,
    borderWidth: 1,
    borderRadius: 10,
    paddingVertical: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  actionButtonText: {
    ...REDESIGN_TYPOGRAPHY.captionMedium,
  },
  removeButton: {
    flex: 1,
    backgroundColor: 'rgba(245,85,96,0.06)',
    borderWidth: 1,
    borderColor: 'rgba(245,85,96,0.12)',
    borderRadius: 10,
    paddingVertical: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  removeButtonText: {
    ...REDESIGN_TYPOGRAPHY.captionMedium,
  },
  setupContainer: {
    gap: 14,
  },
  setupDescription: {
    ...REDESIGN_TYPOGRAPHY.body,
  },
  setupButton: {
    borderRadius: REDESIGN_SPACING.buttonRadius,
    height: REDESIGN_SPACING.buttonHeight,
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  },
  setupButtonDisabled: {
    opacity: 0.4,
  },
  setupButtonText: {
    ...REDESIGN_TYPOGRAPHY.button,
    color: '#FFFFFF',
  },
});
